// Copyright (c) 2026 Solartek LLC. All rights reserved.
/**
 * Meetings — rooms, meetings with required / optional attendees and whole teams, attendance check-in, extensions and the
 * productivity analytics (workforce controls §7, customer request #7; migration 20261025120500).
 *
 *  - `meeting_rooms`: bookable rooms (meeting.manage); every holder of a meeting key reads them.
 *  - `meetings`: planned on a local date with a start and end time in the meeting's timezone (the room's branch, else the
 *    organiser's branch, else the organisation's); stored in UTC. SCHEDULED → IN_PROGRESS (Start) → COMPLETED (End, or the
 *    sweep `autoCloseAfterMinutes` after the expected end) — or CANCELLED. A room is never double-booked (409
 *    MEETING_ROOM_CONFLICT). The expected end is the scheduled end plus the extensions (≤ `maxExtensionMinutes` in all).
 *  - `meeting_attendees`: the organiser (role ORGANIZER) and the invited employees (REQUIRED / OPTIONAL); a team invitation
 *    adds the team's active members and remembers the team. Attendance: a check-in within the grace minutes after the start
 *    is PRESENT, a later one LATE (with the minutes late); ending the meeting makes every attendee still PENDING ABSENT.
 * The meetings settings group (`meetingSettingsSchema`, organizations.ts) holds the timing rules and the notices.
 */
import { z } from 'zod';
import { RECORD_STATUSES } from '../enums.js';
import { booleanQuerySchema, codeSchema, isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
export const MEETING_STATUSES = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
/** A meeting that still holds its room and can be changed (the sweep reminds, notices and closes these). */
export const OPEN_MEETING_STATUSES = ['SCHEDULED', 'IN_PROGRESS'];
export const MEETING_ATTENDEE_ROLES = ['ORGANIZER', 'REQUIRED', 'OPTIONAL'];
/** The roles an invitation gives (the organiser's row is added by the server). */
export const MEETING_INVITE_ROLES = ['REQUIRED', 'OPTIONAL'];
export const MEETING_ATTENDANCE_STATUSES = ['PENDING', 'PRESENT', 'LATE', 'ABSENT', 'EXCUSED'];
/** What the organiser may mark. */
export const MEETING_MARK_STATUSES = ['PRESENT', 'LATE', 'ABSENT', 'EXCUSED'];
export const MEETING_RESPONSES = ['PENDING', 'ACCEPTED', 'DECLINED'];
export const MEETING_CHECK_IN_METHODS = ['SELF', 'ORGANIZER'];
export const MEETING_ANALYTICS_GROUPS = ['day', 'week', 'month'];
/**
 * Whose meetings the analytics cover: `branch` — every meeting in the caller's branch scope (meeting.view / meeting.manage);
 * `organized` — only the meetings the caller organises (meeting.organize alone, e.g. a line manager).
 */
export const MEETING_ANALYTICS_SCOPES = ['branch', 'organized'];
/** Stable `details.reason` codes of the meetings API's refusals. */
export const MEETING_ERROR_REASONS = {
    /** 409: the room is booked by another meeting in the window (`details.meetingId`, `startsAt`, `endsAt`). */
    roomConflict: 'MEETING_ROOM_CONFLICT',
    /** 403 FEATURE_DISABLED: Settings → Meetings is switched off (history stays readable). */
    disabled: 'MEETINGS_DISABLED',
    /** 409 INVALID_STATE: the extension would take the meeting past `maxExtensionMinutes`. */
    extensionLimit: 'MEETING_EXTENSION_LIMIT',
    /** 409 INVALID_STATE: self check-in is closed (`details.state`: NOT_YET | CLOSED | NOT_ALLOWED). */
    checkInClosed: 'MEETING_CHECK_IN_CLOSED',
};
/** A meeting's local clock time (HH:mm, 00:00–23:59) — the window lies within one day. */
export const meetingClockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm');
const clockMinutes = (v) => Number(v.slice(0, 2)) * 60 + Number(v.slice(3, 5));
/** True when `end` is after `start` (both HH:mm of the same day). */
export const meetingWindowValid = (start, end) => clockMinutes(end) > clockMinutes(start);
// ----- rooms -------------------------------------------------------------------------------------------------------------------
const roomFields = {
    name: z.string().trim().min(1).max(120),
    nameAr: z.string().trim().max(120).nullable().optional(),
    /** The branch the room belongs to (its meetings take that branch and timezone); null = organisation-wide. */
    branchId: uuidSchema.nullable().optional(),
    capacity: z.number().int().min(1).max(10_000).nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    equipment: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
    status: z.enum(RECORD_STATUSES).optional(),
};
export const meetingRoomInputSchema = z.object({ code: codeSchema, ...roomFields });
/** PATCH body (explicit, no defaults: a one-field PATCH changes one field). */
export const meetingRoomUpdateSchema = z.object({ ...roomFields, code: codeSchema.optional(), name: roomFields.name.optional() });
export const meetingRoomListQuerySchema = z.object({ includeInactive: booleanQuerySchema.optional(), branchId: uuidSchema.optional() });
// ----- meetings ----------------------------------------------------------------------------------------------------------------
export const meetingAttendeeSpecSchema = z.object({ employeeId: uuidSchema, role: z.enum(MEETING_INVITE_ROLES) });
/** A whole team: its active members are invited with the role, the team is remembered on each row. */
export const meetingTeamSpecSchema = z.object({ teamId: uuidSchema, role: z.enum(MEETING_INVITE_ROLES) });
const meetingFields = {
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(4000).nullable().optional(),
    roomId: uuidSchema.nullable().optional(),
    /** Free text where there is no room (another site, a call). */
    location: z.string().trim().max(200).nullable().optional(),
    /** Minutes after the start a check-in still counts as on time. Absent: the organisation's `defaultGraceMinutes`. */
    graceMinutes: z.number().int().min(0).max(60).optional(),
};
export const meetingCreateSchema = z.object({
    ...meetingFields,
    /** Local date and times in the meeting's timezone (the room's branch, else the organiser's, else the organisation's). */
    date: isoDateSchema,
    startTime: meetingClockSchema,
    endTime: meetingClockSchema,
    /**
     * The organiser: the caller's own employee record when absent. Only a meeting.manage holder may name somebody else (and must,
     * when their login is linked to no employee).
     */
    organizerEmployeeId: uuidSchema.optional(),
    attendees: z.array(meetingAttendeeSpecSchema).max(500).optional(),
    teams: z.array(meetingTeamSpecSchema).max(50).optional(),
}).superRefine((v, ctx) => {
    if (!meetingWindowValid(v.startTime, v.endTime))
        ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'The end must be after the start' });
});
/**
 * PATCH body — explicit optional fields and no defaults (Zod 4: `.partial()` of a schema with defaults re-applies them). A time
 * change sends the date and both times; attendees change through their own endpoints.
 */
export const meetingUpdateSchema = z.object({
    title: meetingFields.title.optional(),
    description: meetingFields.description,
    roomId: meetingFields.roomId,
    location: meetingFields.location,
    graceMinutes: meetingFields.graceMinutes,
    date: isoDateSchema.optional(),
    startTime: meetingClockSchema.optional(),
    endTime: meetingClockSchema.optional(),
}).superRefine((v, ctx) => {
    if (v.startTime && v.endTime && !meetingWindowValid(v.startTime, v.endTime))
        ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'The end must be after the start' });
});
export const meetingCancelSchema = z.object({ reason: z.string().trim().min(3).max(1000) });
export const meetingExtendSchema = z.object({
    minutes: z.number().int().min(1).max(480),
    reason: z.string().trim().max(500).nullable().optional(),
});
export const meetingAttendeesAddSchema = z.object({
    attendees: z.array(meetingAttendeeSpecSchema).max(500).optional(),
    teams: z.array(meetingTeamSpecSchema).max(50).optional(),
}).refine((v) => (v.attendees?.length ?? 0) + (v.teams?.length ?? 0) > 0, { path: ['attendees'], message: 'Add at least one employee or team' });
/** The organiser marks one attendee. `excuse` is kept with EXCUSED (and ABSENT); LATE takes the minutes from the check-in. */
export const meetingMarkSchema = z.object({
    status: z.enum(MEETING_MARK_STATUSES),
    excuse: z.string().trim().max(500).nullable().optional(),
});
export const meetingRespondSchema = z.object({ response: z.enum(['ACCEPTED', 'DECLINED']) });
export const meetingListQuerySchema = paginationQuerySchema.extend({
    /** Local dates of the meetings (inclusive). */
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    roomId: uuidSchema.optional(),
    status: z.enum(MEETING_STATUSES).optional(),
    organizerEmployeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    /** Only the meetings the caller organises or is invited to. */
    mine: booleanQuerySchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const selfMeetingsQuerySchema = z.object({
    /** History from this local date (default: 90 days back). */
    from: isoDateSchema.optional(),
});
// ----- analytics ----------------------------------------------------------------------------------------------------------------
export const meetingAnalyticsQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    groupBy: z.enum(MEETING_ANALYTICS_GROUPS).optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    roomId: uuidSchema.optional(),
    /** The `branch` scope only: in the `organized` scope the organiser is always the caller (the server sets it). */
    organizerEmployeeId: uuidSchema.optional(),
}).refine((v) => !v.from || !v.to || v.from <= v.to, { path: ['to'], message: 'The end of the range cannot be before its start' });
// ----- invitee picker -----------------------------------------------------------------------------------------------------------
export const meetingInviteeQuerySchema = z.object({ search: z.string().trim().max(100).optional() });
//# sourceMappingURL=meetings.js.map